<template>
  <div class="p-6 bg-white rounded-lg shadow-md">
    <h2 class="text-2xl font-bold mb-4">Supabase Storage Test</h2>
    
    <div v-if="loading" class="mb-4 p-3 bg-blue-100 text-blue-700 rounded-md">
      Loading...
    </div>
    
    <div v-if="error" class="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
      Error: {{ error.message }}
    </div>
    
    <!-- Storage Connection Test -->
    <div class="mt-6">
      <h3 class="text-xl font-bold mb-2">Storage Connection Test</h3>
      <button
        @click="testStorageConnection"
        class="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors"
        :disabled="loading"
      >
        <span v-if="loading">Testing...</span>
        <span v-else>Test Storage Connection</span>
      </button>
      
      <div v-if="storageTestResult" class="mt-4">
        <div v-if="storageTestResult.success" class="p-4 bg-green-100 text-green-700 rounded-md">
          <h4 class="font-bold">Storage Connection Successful!</h4>
          <pre class="whitespace-pre-wrap">{{ JSON.stringify(storageTestResult.data, null, 2) }}</pre>
        </div>
        <div v-else class="p-4 bg-red-100 text-red-700 rounded-md">
          <h4 class="font-bold">Storage Connection Error</h4>
          <p>{{ storageTestResult.message }}</p>
          
          <div v-if="storageTestResult.error && storageTestResult.error.statusCode === 404" class="mt-4 border-t border-red-200 pt-4">
            <h5 class="font-bold">Missing Bucket Error</h5>
            <p class="mb-2">The required storage buckets have not been created yet. Please create the following buckets in your Supabase instance:</p>
            <ul class="list-disc pl-5 mb-4">
              <li>templates</li>
              <li>documents</li>
              <li>calendars</li>
              <li>avatars</li>
              <li>reports</li>
              <li>forecasts</li>
            </ul>
            <p>You can create these buckets in the Supabase dashboard under Storage.</p>
          </div>
          
          <pre v-if="storageTestResult.error" class="mt-4 whitespace-pre-wrap bg-red-50 p-2 rounded">{{ JSON.stringify(storageTestResult.error, null, 2) }}</pre>
        </div>
      </div>
    </div>
    
    <!-- File Upload Test -->
    <div class="mt-8 border-t border-gray-200 pt-6">
      <h3 class="text-xl font-bold mb-4">File Upload Test</h3>
      
      <div class="mb-4">
        <label for="bucket-select" class="block text-sm font-medium mb-1">Select Bucket</label>
        <select
          id="bucket-select"
          v-model="selectedBucket"
          class="w-full px-3 py-2 border border-gray-300 rounded-md"
        >
          <option :value="StorageBucket.TEMPLATES">Templates</option>
          <option :value="StorageBucket.DOCUMENTS">Documents</option>
          <option :value="StorageBucket.CALENDARS">Calendars</option>
          <option :value="StorageBucket.AVATARS">Avatars</option>
          <option :value="StorageBucket.REPORTS">Reports</option>
          <option :value="StorageBucket.FORECASTS">Forecasts</option>
        </select>
      </div>
      
      <div class="mb-4">
        <label for="file-upload" class="block text-sm font-medium mb-1">Select File</label>
        <input 
          type="file" 
          id="file-upload" 
          @change="handleFileChange"
          class="w-full px-3 py-2 border border-gray-300 rounded-md"
        >
      </div>
      
      <button
        @click="uploadFile"
        class="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
        :disabled="loading || !selectedFile"
      >
        <span v-if="loading">Uploading...</span>
        <span v-else>Upload File</span>
      </button>
      
      <div v-if="uploadResult" class="mt-4">
        <div v-if="uploadResult.success" class="p-4 bg-green-100 text-green-700 rounded-md">
          <h4 class="font-bold">File Upload Successful!</h4>
          <p><strong>Original filename:</strong> {{ uploadResult.originalName }}</p>
          <p><strong>Sanitized path:</strong> {{ uploadResult.path }}</p>
          <div v-if="uploadResult.publicUrl" class="mt-2">
            <p class="mb-2">Public URL:</p>
            <a :href="uploadResult.publicUrl" target="_blank" class="text-blue-600 underline">{{ uploadResult.publicUrl }}</a>
          </div>
        </div>
        <div v-else class="p-4 bg-red-100 text-red-700 rounded-md">
          <h4 class="font-bold">File Upload Error</h4>
          <p>{{ uploadResult.message }}</p>
          
          <div v-if="uploadResult.isBucketNotFound" class="mt-4 border-t border-red-200 pt-4">
            <h5 class="font-bold">Bucket Not Found</h5>
            <p class="mb-2">The storage bucket doesn't exist in your Supabase project. Please create it:</p>
            <ol class="list-decimal pl-5 mb-4">
              <li>Go to your Supabase project dashboard</li>
              <li>Navigate to the "Storage" section</li>
              <li>Click "Create a new bucket"</li>
              <li>Create a bucket named <code>{{ selectedBucket }}</code></li>
              <li>Set the appropriate permissions for the bucket</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
    
    <!-- File List -->
    <div class="mt-8 border-t border-gray-200 pt-6">
      <h3 class="text-xl font-bold mb-4">File List</h3>
      
      <div class="mb-4">
        <label for="list-bucket-select" class="block text-sm font-medium mb-1">Select Bucket</label>
        <select
          id="list-bucket-select"
          v-model="listBucket"
          class="w-full px-3 py-2 border border-gray-300 rounded-md"
        >
          <option :value="StorageBucket.TEMPLATES">Templates</option>
          <option :value="StorageBucket.DOCUMENTS">Documents</option>
          <option :value="StorageBucket.CALENDARS">Calendars</option>
          <option :value="StorageBucket.AVATARS">Avatars</option>
          <option :value="StorageBucket.REPORTS">Reports</option>
          <option :value="StorageBucket.FORECASTS">Forecasts</option>
        </select>
      </div>
      
      <button
        @click="listFiles"
        class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
        :disabled="loading"
      >
        <span v-if="loading">Loading...</span>
        <span v-else>List Files</span>
      </button>
      
      <div v-if="fileList" class="mt-4">
        <div v-if="fileList.length > 0" class="p-4 bg-gray-100 rounded-md">
          <h4 class="font-bold mb-2">Files in {{ listBucket }} bucket:</h4>
          <ul class="divide-y divide-gray-200">
            <li v-for="file in fileList" :key="file.name" class="py-2">
              <div class="flex justify-between items-center">
                <div>
                  <p class="font-medium">{{ file.name }}</p>
                  <p class="text-sm text-gray-500">Size: {{ formatFileSize(file.metadata?.size) }}</p>
                </div>
                <div class="flex space-x-2">
                  <button
                    @click="getFileUrl(file.name)"
                    class="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                  >
                    View
                  </button>
                  <button
                    @click="confirmDelete(file.name)"
                    class="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </div>
        <div v-else class="p-4 bg-yellow-100 text-yellow-700 rounded-md">
          <p>No files found in the {{ listBucket }} bucket.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useStorage } from '@/composables/useStorage';
import { useAuth } from '@/composables/useAuth';
import { supabase } from '@/services/supabase';
import { StorageTestResult, FileUploadResult, FileMetadata, StorageBucket } from '@/types/storage';
import { logger, LogCategory } from '@/services/logger';
import { handleError, ErrorType, withErrorHandling } from '@/utils/errorHandling';

export default defineComponent({
  name: 'StorageTest',
  setup() {
    const { loading, error, uploadFile: uploadStorageFile, listFiles: listStorageFiles, getPublicUrl, deleteFile: deleteStorageFile } = useStorage();
    const { checkAndRefreshAuth } = useAuth();
    
    const storageTestResult = ref<StorageTestResult | null>(null);
    const selectedBucket = ref<StorageBucket>(StorageBucket.TEMPLATES);
    const selectedFile = ref<File | null>(null);
    const uploadResult = ref<FileUploadResult | null>(null);
    const listBucket = ref<StorageBucket>(StorageBucket.TEMPLATES);
    const fileList = ref<FileMetadata[]>([]);
    
    // Get bucket names from environment variables or use enum defaults
    const templatesBucket = process.env.VUE_APP_SUPABASE_TEMPLATES_BUCKET || StorageBucket.TEMPLATES;
    
    async function testStorageConnection() {
      storageTestResult.value = null;
      
      await withErrorHandling(async () => {
        // Check and refresh authentication if needed
        const isAuthenticated = await checkAndRefreshAuth();
        if (!isAuthenticated) {
          logger.warn('User is not authenticated, storage test may fail', LogCategory.AUTH);
        }
        
        // Test if we can list files in a bucket
        const { data, error: storageError } = await supabase.storage
          .from(templatesBucket)
          .list();
        
        if (storageError) {
          throw handleError(storageError, error, ErrorType.STORAGE,
            'Failed to connect to storage service', { bucket: templatesBucket });
        }
        
        storageTestResult.value = {
          success: true,
          message: 'Storage connection successful',
          data
        };
        
        logger.info('Storage connection test successful', LogCategory.STORAGE, { bucket: templatesBucket });
        return data;
      }, error, ErrorType.STORAGE, 'Storage connection test failed', { bucket: templatesBucket })
      .catch(err => {
        storageTestResult.value = {
          success: false,
          message: err.message || 'Unknown error',
          error: err
        };
      });
    }
    
    function handleFileChange(event: Event) {
      const input = event.target as HTMLInputElement;
      if (input.files && input.files.length > 0) {
        selectedFile.value = input.files[0];
      }
    }
    
    async function uploadFile() {
      if (!selectedFile.value) return;
      
      uploadResult.value = null;
      
      await withErrorHandling(async () => {
        // Check and refresh authentication if needed
        const isAuthenticated = await checkAndRefreshAuth();
        if (!isAuthenticated) {
          throw handleError(new Error('Authentication required'), error, ErrorType.AUTHENTICATION);
        }
        
        // Generate a unique file path
        const timestamp = new Date().getTime();
        const fileName = `${timestamp}-${selectedFile.value!.name}`;
        
        // Upload the file
        logger.info('Uploading file', LogCategory.STORAGE, {
          bucket: selectedBucket.value,
          fileName,
          originalName: selectedFile.value!.name,
          size: selectedFile.value!.size
        });
        
        const data = await uploadStorageFile(selectedBucket.value, fileName, selectedFile.value!);
        
        // Get the public URL using the sanitized path returned from the upload function
        const sanitizedPath = data.path || fileName;
        const publicUrl = getPublicUrl(selectedBucket.value, sanitizedPath);
        
        uploadResult.value = {
          success: true,
          path: sanitizedPath,
          originalName: selectedFile.value!.name,
          publicUrl
        };
        
        logger.info('File uploaded successfully', LogCategory.STORAGE, {
          bucket: selectedBucket.value,
          path: sanitizedPath
        });
        
        // Reset the file input
        selectedFile.value = null;
        const fileInput = document.getElementById('file-upload') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        
        return data;
      }, error, ErrorType.STORAGE, 'File upload failed', { bucket: selectedBucket.value })
      .catch(err => {
        // Check if it's a bucket not found error
        const errorMessage = err.message || 'Unknown error';
        const isBucketNotFound = errorMessage.includes('Bucket not found');
        
        uploadResult.value = {
          success: false,
          message: errorMessage,
          isBucketNotFound,
          error: err
        };
      });
    }
    
    async function listFiles() {
      fileList.value = [];
      
      await withErrorHandling(async () => {
        // Check and refresh authentication if needed
        const isAuthenticated = await checkAndRefreshAuth();
        if (!isAuthenticated) {
          throw handleError(new Error('Authentication required'), error, ErrorType.AUTHENTICATION);
        }
        
        logger.info('Listing files', LogCategory.STORAGE, { bucket: listBucket.value });
        
        const data = await listStorageFiles(listBucket.value);
        
        fileList.value = data || [];
        
        logger.info(`Found ${fileList.value.length} files`, LogCategory.STORAGE, {
          bucket: listBucket.value,
          count: fileList.value.length
        });
        
        return data;
      }, error, ErrorType.STORAGE, 'Failed to list files', { bucket: listBucket.value });
    }
    
    async function getFileUrl(fileName: string) {
      await withErrorHandling(async () => {
        // Check and refresh authentication if needed
        await checkAndRefreshAuth();
        
        logger.info('Getting public URL for file', LogCategory.STORAGE, {
          bucket: listBucket.value,
          fileName
        });
        
        const publicUrl = getPublicUrl(listBucket.value, fileName);
        window.open(publicUrl, '_blank');
        
        return publicUrl;
      }, error, ErrorType.STORAGE, 'Failed to get file URL', { bucket: listBucket.value, fileName });
    }
    
    async function confirmDelete(fileName: string) {
      if (confirm(`Are you sure you want to delete ${fileName}?`)) {
        await withErrorHandling(async () => {
          // Check and refresh authentication if needed
          const isAuthenticated = await checkAndRefreshAuth();
          if (!isAuthenticated) {
            throw handleError(new Error('Authentication required'), error, ErrorType.AUTHENTICATION);
          }
          
          logger.info('Deleting file', LogCategory.STORAGE, {
            bucket: listBucket.value,
            fileName
          });
          
          await deleteStorageFile(listBucket.value, fileName);
          
          logger.info('File deleted successfully', LogCategory.STORAGE, {
            bucket: listBucket.value,
            fileName
          });
          
          // Refresh the file list
          await listFiles();
          
          return true;
        }, error, ErrorType.STORAGE, 'Failed to delete file', { bucket: listBucket.value, fileName });
      }
    }
    
    function formatFileSize(bytes?: number): string {
      if (!bytes) return 'Unknown';
      
      const units = ['B', 'KB', 'MB', 'GB', 'TB'];
      let size = bytes;
      let unitIndex = 0;
      
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
      }
      
      return `${size.toFixed(2)} ${units[unitIndex]}`;
    }
    // Check authentication when component is mounted
    onMounted(async () => {
      try {
        logger.info('StorageTest component mounted, checking authentication', LogCategory.AUTH);
        await checkAndRefreshAuth();
        // Automatically list files when component is mounted
        await listFiles();
      } catch (err) {
        logger.error('Error checking authentication on mount', LogCategory.AUTH, { error: err });
      }
    });
    return {
      loading,
      error,
      storageTestResult,
      selectedBucket,
      selectedFile,
      uploadResult,
      listBucket,
      fileList,
      testStorageConnection,
      handleFileChange,
      uploadFile,
      listFiles,
      getFileUrl,
      confirmDelete,
      formatFileSize,
      // Make enum available in template
      StorageBucket
    };
  }
});
</script>
